/**
 * Citation OUTPUT-language locales.
 *
 * This is the language of the citation itself — "n.d." vs "s. f." vs "日付なし", "Ed."
 * vs "Hrsg." vs "编" — not the language of the site UI. The two are separate on purpose:
 * a Spanish speaker may well be writing an English paper, and a student at an English
 * university may need Portuguese references.
 *
 * Every locale here is available OFFLINE. Five come bundled with
 * `@citation-js/plugin-csl` (`lib/locales.json` → en-US, nl-NL, fr-FR, de-DE, es-ES);
 * the other twelve are vendored into `src/data/csl-locales/` by
 * `scripts/generate-csl-locales.mjs` and registered on first use. Nothing here triggers
 * a network fetch, which is what keeps the golden test suite's no-network guarantee
 * (`csl-stub.ts`) intact and citation output snapshot-deterministic.
 *
 * English (`en-US`) is the default and MUST stay the no-op path so existing users get
 * byte-for-byte identical output (HARD RULE #1 — see golden tests). It is bundled, so
 * it never goes near the registration code below.
 *
 * This module stays dependency-free (no Citation.js import) so the client citation tool
 * can render the selector without pulling the engine into the bundle.
 */
export const DEFAULT_OUTPUT_LOCALE = "en-US";

/**
 * Locales citeproc already has. Registering one of these again would be pointless work
 * and would risk drifting from the definition the engine ships.
 */
export const BUNDLED_OUTPUT_LOCALES = [
  "en-US",
  "es-ES",
  "fr-FR",
  "de-DE",
  "nl-NL",
] as const;

/**
 * Ordered for the selector: English first, then by the size of the audience that writes
 * academic work in each language. Labels are `Native (English)` so a reader recognises
 * their own language and an English speaker can still tell what they are picking.
 */
export const OUTPUT_LOCALES = [
  { code: "en-US", label: "English" },
  { code: "es-ES", label: "Español (Spanish)" },
  { code: "pt-BR", label: "Português (Portuguese)" },
  { code: "fr-FR", label: "Français (French)" },
  { code: "de-DE", label: "Deutsch (German)" },
  { code: "it-IT", label: "Italiano (Italian)" },
  { code: "nl-NL", label: "Nederlands (Dutch)" },
  { code: "pl-PL", label: "Polski (Polish)" },
  { code: "tr-TR", label: "Türkçe (Turkish)" },
  { code: "ru-RU", label: "Русский (Russian)" },
  { code: "id-ID", label: "Bahasa Indonesia (Indonesian)" },
  { code: "vi-VN", label: "Tiếng Việt (Vietnamese)" },
  { code: "hi-IN", label: "हिन्दी (Hindi)" },
  { code: "ja-JP", label: "日本語 (Japanese)" },
  { code: "ko-KR", label: "한국어 (Korean)" },
  { code: "zh-CN", label: "简体中文 (Chinese, Simplified)" },
  { code: "ar", label: "العربية (Arabic)" },
] as const;

export type OutputLocale = (typeof OUTPUT_LOCALES)[number]["code"];

const OUTPUT_LOCALE_CODES = new Set<string>(OUTPUT_LOCALES.map((l) => l.code));
const BUNDLED = new Set<string>(BUNDLED_OUTPUT_LOCALES);

/** Type guard: true only for an offline-capable output locale. */
export function isAllowedOutputLocale(lang: unknown): lang is OutputLocale {
  return typeof lang === "string" && OUTPUT_LOCALE_CODES.has(lang);
}

/** True when citeproc already has this locale and no registration is needed. */
export function isBundledOutputLocale(lang: string): boolean {
  return BUNDLED.has(lang);
}

/**
 * Validate a requested output locale, falling back to English for anything unsupported
 * (or undefined/invalid). Never throws and never triggers a network fetch — an unknown
 * locale silently degrades to the default `en-US` path.
 */
export function normalizeOutputLocale(lang: unknown): OutputLocale {
  return isAllowedOutputLocale(lang) ? lang : DEFAULT_OUTPUT_LOCALE;
}

/**
 * The best citation-output locale for a site UI language, or English if the CSL project
 * publishes no locale for it.
 *
 * Used to pre-select the selector on a localized page: someone reading the site in
 * Portuguese is more likely than not to want "s.d." rather than "n.d.". It is a default,
 * not a lock — the selector is right there.
 */
const UI_TO_OUTPUT_LOCALE: Record<string, OutputLocale> = {
  en: "en-US",
  es: "es-ES",
  pt: "pt-BR",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
  nl: "nl-NL",
  pl: "pl-PL",
  tr: "tr-TR",
  ru: "ru-RU",
  id: "id-ID",
  vi: "vi-VN",
  hi: "hi-IN",
  ja: "ja-JP",
  ko: "ko-KR",
  zh: "zh-CN",
  ar: "ar",
};

export function outputLocaleForUiLocale(uiLocale: string): OutputLocale {
  return UI_TO_OUTPUT_LOCALE[uiLocale] ?? DEFAULT_OUTPUT_LOCALE;
}
